<template>
  <Teleport to="body">
    <div class="menu-backdrop" :class="{ show: showMenu > 0 }" @click="showMenu = 0" />
  </Teleport>

  <nav class="menu-stacked" :class="{ show: showMenu > 0 }">
    <div class="menu-content">
      <div class="menu-items">
        <h5 v-for="page in navigationPages" :key="page?.id">
          <router-link
            @click="showMenu = 0"
            :to="pathName(page?.url)"
            v-html="page?.title"
            class="nav-button"
            active-class="active"
            exact-active-class="active"
          />
        </h5>
      </div>

      <div class="footer-items" v-if="footerPages.length > 0">
        <h5 v-for="page in footerPages" :key="page?.id">
          <router-link
            @click="showMenu = 0"
            :to="pathName(page?.url)"
            v-html="page?.title"
            class="footer-nav-button"
            active-class="active"
            exact-active-class="active"
          />
        </h5>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { useStore } from '@/KQL.js'
import { yamlToArray, pageFromPath, pathName } from '@/utils'
import { composables } from '@/composables'
import { computed } from 'vue'

const showMenu = composables().showMenu
const kql = useStore()

const navigationPages = computed(() => {
  if (!kql.site?.content?.main_navigation || !kql.pages) return []

  return yamlToArray(kql.site.content.main_navigation).map(uid => pageFromPath(uid, kql.pages)).filter(page => page)
})

const footerPages = computed(() => {
  if (!kql.site?.content?.footer_navigation || !kql.pages) return []

  return yamlToArray(kql.site.content.footer_navigation).map(uid => pageFromPath(uid, kql.pages)).filter(page => page)
})
</script>

<style scoped>
.menu-stacked {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
}

.menu-stacked.show {
  opacity: 1;
  visibility: visible;
}

.menu-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 110vh;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 1;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
}

.menu-backdrop.show {
  opacity: 1;
  visibility: visible;
}

.menu-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  background-color: transparent;
  transform: translateY(100%);
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
}

.menu-stacked.show .menu-content {
  transform: translateY(0);
}

.menu-items {
  padding: 3rem 2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

h5 {
  margin: 0;
  text-align: left;
}

:deep(.nav-button) {
  font-family: Kessler;
  display: block;
  color: var(--dark, #333);
  text-decoration: none;
  text-transform: none;
  font-size: var(--large);
  line-height: 1.1;
  transition: color 0.2s ease-in-out;
  text-align: left;
}

:deep(.nav-button:hover) {
  color: var(--accent, #666);
}

:deep(.nav-button.active) {
  color: var(--dark, #333);
}

.footer-items {
  padding: 1rem 2rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

:deep(.footer-nav-button) {
  font-family: inherit;
  display: block;
  color: var(--dark, #333);
  text-decoration: none;
  text-transform: none;
  font-weight: 400;
  font-size: var(--medium);
  line-height: 1.3;
  transition: color 0.2s ease-in-out;
  text-align: left;
  opacity: 0.8;
}

:deep(.footer-nav-button:hover) {
  color: var(--accent, #666);
  opacity: 1;
}

:deep(.footer-nav-button.active) {
  color: var(--dark, #333);
  font-weight: 500;
  opacity: 1;
}
</style>
